import { useMemo } from "react";
import {
  StudioFeatures,
  type StudioFeatureDefinition,
  type StudioFeatureKey,
} from "@intelliextract-studio/shared";
import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";

function isFeatureAccessible(
  feature: StudioFeatureDefinition,
  sessionFeatures: string[],
  authConfigured: boolean,
): boolean {
  const config = WebAppConfig.getInstance();

  if (!config.isFeatureFlagEnabled(feature.key)) {
    return false;
  }

  if (!authConfigured) {
    return true;
  }

  return sessionFeatures.includes(feature.key);
}

export function useFeatures() {
  const config = WebAppConfig.getInstance();
  const authConfigured = config.isAuthConfigured();
  const sessionFeatures = useMemo(() => sessionRepository.getUserFeatures(), []);

  const accessibleFeatures = useMemo(
    () =>
      StudioFeatures.ALL.filter((feature) =>
        isFeatureAccessible(feature, sessionFeatures, authConfigured),
      ),
    [authConfigured, sessionFeatures],
  );

  const hasFeature = (key: StudioFeatureKey): boolean =>
    accessibleFeatures.some((feature) => feature.key === key);

  const firstAccessibleRoute =
    accessibleFeatures[0]?.routePath ?? "/access-denied";

  return {
    accessibleFeatures,
    hasFeature,
    firstAccessibleRoute,
    sessionFeatures,
  };
}
